// ═══════════════════════════════════════════════════════════════════════════════
// Family member list / form visuals (icons + hashtag split + birthday summary text)
// ═══════════════════════════════════════════════════════════════════════════════

import {
  calculateAge,
  isFullFamilyMemberBirthday,
  isPartialFamilyMemberBirthday,
} from '@fdh/shared';
import type { MessageParams } from '../../i18n/uiMessages';

export const splitHashtagInput = (raw: string): string[] =>
  raw
    .split(/[\n,，、;；]+/)
    .map((piece) => piece.trim().replace(/^#+/u, ''))
    .filter((piece) => piece.length > 0);

type Translate = (key: string, params?: MessageParams) => string;

export const buildFamilyMemberBirthdaySummary = (birthday: string, t: Translate): string => {
  const s = birthday.trim();
  if (s === '') {
    return t('family.display.birthdaySkipped');
  }
  if (isFullFamilyMemberBirthday(s)) {
    const age = calculateAge(s);
    const year = Number(s.slice(0, 4));
    if (age === null) {
      return s;
    }
    return t('family.display.ageWithYear', { age, year });
  }
  if (isPartialFamilyMemberBirthday(s)) {
    const month = Number(s.slice(2, 4));
    const day = Number(s.slice(5, 7));
    return t('family.display.birthdayMonthDay', { month, day });
  }
  return t('family.display.birthdaySkipped');
};

const iconClass = 'h-5 w-5 shrink-0 text-gray-400 dark:text-gray-500';

export const IconCalendar = () => (
  <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
    <path
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8 7V5m8 2V5m-9 9h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

export const IconBirthdayCake = () => (
  <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
    <path
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4 10h16v8a2 2 0 01-2 2H6a2 2 0 01-2-2v-8zM8 10V7a2 2 0 114 0v3M12 10V7a2 2 0 114 0v3M9 17h.01M15 17h.01"
    />
    <path strokeWidth={1.75} strokeLinecap="round" d="M4 10l1.5-2h13L20 10" />
  </svg>
);

export const IconDocument = () => (
  <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
    <path
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12h6M9 16h6M7 4h7l4 4v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2z"
    />
  </svg>
);

export const IconUtensils = () => (
  <svg className={iconClass} viewBox="0 -3.84 122.88 122.88" fill="currentColor" aria-hidden>
    <path d="M29.03,100.46l20.79-25.21l9.51,12.13L41,110.69C33.98,119.61,20.99,110.21,29.03,100.46L29.03,100.46z M53.31,43.05 c1.98-6.46,1.07-11.98-6.37-20.18L28.76,1c-2.58-3.03-8.66,1.42-6.12,5.09L37.18,24c2.75,3.34-2.36,7.76-5.2,4.32L16.94,9.8 c-2.8-3.21-8.59,1.03-5.66,4.7c4.24,5.1,10.8,13.43,15.04,18.53c2.94,2.99-1.53,7.42-4.43,3.69L6.96,18.32 c-2.19-2.38-5.77-0.9-6.72,1.88c-1.02,2.97,1.49,5.14,3.2,7.34L20.1,49.06c5.17,5.99,10.95,9.54,17.67,7.53 c1.03-0.31,2.29-0.94,3.64-1.77l44.76,57.78c2.41,3.11,7.06,3.44,10.08,0.93l0.69-0.57c3.4-2.83,3.95-8,1.04-11.34L50.58,47.16 C51.96,45.62,52.97,44.16,53.31,43.05L53.31,43.05z M65.98,55.65l7.37-8.94C63.87,23.21,99-8.11,116.03,6.29 C136.72,23.8,105.97,66,84.36,55.57l-8.73,11.09L65.98,55.65L65.98,55.65z" />
  </svg>
);

export const IconAlertTriangle = () => (
  <svg className={iconClass} viewBox="0 0 512 512" fill="none" stroke="currentColor" aria-hidden>
    <circle cx="256" cy="256" r="236" strokeWidth={30} />
    <g strokeWidth={20}>
      <line x1="126.6" y1="385.4" x2="75" y2="437" />
      <line x1="437" y1="75" x2="367" y2="145" />
      <path d="M164.6,158.7h-40.7c0,0-18.9,0-18.9-18.9" />
      <line x1="273.3" y1="274.4" x2="389" y2="274.4" />
      <line x1="273.3" y1="274.4" x2="273.3" y2="390.2" />
      <line x1="273.3" y1="274.4" x2="355.1" y2="356.2" />
      <line x1="273.3" y1="274.4" x2="333.1" y2="175.4" />
      <path d="M273.9,275c-17.6,41.5-57.4,68.7-105.3,68.7c-14.3,0-27.9-2.6-40.5-7.3 c-4.1-1.5-5.6-6.5-3-10l23.6-31.4c3.2-4.3,10.2-2.9,11.2,2.4C171,353,222.1,394.2,282,389.9c54.9-4,100.3-47.5,106.3-102.2 c7.7-69.8-46.7-128.9-115-128.9H164.6c-0.3,3.1-0.4,6.2-0.4,9.3C164.2,228.6,214.5,270.1,273.9,275" />
      <path d="M245.4,206.3c0,5.4,4.4,9.9,9.8,9.9c5.4,0,9.9-4.4,9.9-9.9c0-5.4-4.4-9.8-9.9-9.8 C249.8,196.5,245.4,200.9,245.4,206.3z" />
    </g>
  </svg>
);

export const IconMedicalReport = () => (
  <svg className={iconClass} viewBox="-2 0 20 20" fill="none" stroke="currentColor" aria-hidden>
    <g transform="translate(-4 -2)">
      <path
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15,6h3a1,1,0,0,1,1,1V20a1,1,0,0,1-1,1H6a1,1,0,0,1-1-1V7A1,1,0,0,1,6,6H9"
      />
      <path
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14,5h0a2,2,0,0,0-2-2h0a2,2,0,0,0-2,2h0A1,1,0,0,0,9,6V8h6V6A1,1,0,0,0,14,5ZM12,16V12m2,2H10"
      />
    </g>
  </svg>
);

export const IconHeartbeat = IconMedicalReport;

export const IconPill = IconMedicalReport;

export const IconHash = () => (
  <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
    <path
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M10 4L8 20M16 4l-2 16M5 9h14M4 15h14"
    />
  </svg>
);
